import { supabaseAdmin as supabase } from '../../../lib/supabase.js';

class SubjectItemLoadService {
  // Get all subject item loads
  async getSubjectItemLoads(tenantId = '1acd60af-8884-48bb-b1ea-13fbb935d3a8') {
    try {
      const { data, error } = await supabase
        .from('saas_myclass_subject_item_load')
        .select('*')
        .eq('tenant_id', tenantId)
        .order('subject_code')
        .order('class_code');

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching subject item loads:', error);
      return [];
    }
  }

  // Get subject item loads by subject group
  async getSubjectItemLoadsByGroup(subjectGroup, tenantId = '1acd60af-8884-48bb-b1ea-13fbb935d3a8') {
    try {
      const { data, error } = await supabase
        .from('saas_myclass_subject_item_load')
        .select('*')
        .eq('tenant_id', tenantId)
        .eq('subject_group', subjectGroup)
        .order('subject_code')
        .order('class_code');

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching subject item loads by group:', error);
      return [];
    }
  }

  // Update teaching load for multiple items
  async updateTeachingLoads(updates, tenantId = '1acd60af-8884-48bb-b1ea-13fbb935d3a8') {
    try {
      const promises = updates.map(update => 
        supabase
          .from('saas_myclass_subject_item_load')
          .update({ 
            teaching_load: update.teachingLoad,
            updated_at: new Date().toISOString()
          })
          .eq('tenant_id', tenantId)
          .eq('subject_code', update.subjectCode)
          .eq('class_code', update.classCode)
      );

      const results = await Promise.all(promises);
      
      // Check for errors
      const errors = results.filter(result => result.error);
      if (errors.length > 0) {
        throw new Error(`Failed to update ${errors.length} items`);
      }

      return { success: true, updated: updates.length };
    } catch (error) {
      console.error('Error updating teaching loads:', error);
      throw error;
    }
  }

  // Create new subject item load
  async createSubjectItemLoad(itemData, tenantId = '1acd60af-8884-48bb-b1ea-13fbb935d3a8') {
    try {
      const { data, error } = await supabase
        .from('saas_myclass_subject_item_load')
        .insert([{
          ...itemData,
          tenant_id: tenantId,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }])
        .select();

      if (error) throw error;
      return data[0];
    } catch (error) {
      console.error('Error creating subject item load:', error);
      throw error;
    }
  }

  // Delete subject item load
  async deleteSubjectItemLoad(id, tenantId = '1acd60af-8884-48bb-b1ea-13fbb935d3a8') {
    try {
      const { error } = await supabase
        .from('saas_myclass_subject_item_load')
        .delete()
        .eq('id', id)
        .eq('tenant_id', tenantId);

      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Error deleting subject item load:', error);
      throw error;
    }
  }

  // Search subject item loads
  async searchSubjectItemLoads(searchTerm, tenantId = '1acd60af-8884-48bb-b1ea-13fbb935d3a8') {
    try {
      const { data, error } = await supabase
        .from('saas_myclass_subject_item_load')
        .select('*')
        .eq('tenant_id', tenantId)
        .or(`subject_code.ilike.%${searchTerm}%,class_code.ilike.%${searchTerm}%,subject_name.ilike.%${searchTerm}%,class_name.ilike.%${searchTerm}%`)
        .order('subject_code')
        .order('class_code');

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error searching subject item loads:', error);
      return [];
    }
  }

  // Get statistics
  async getStatistics(tenantId = '1acd60af-8884-48bb-b1ea-13fbb935d3a8') {
    try {
      const { data, error } = await supabase
        .from('saas_myclass_subject_item_load')
        .select('subject_group, teaching_load')
        .eq('tenant_id', tenantId);

      if (error) throw error;

      const stats = {
        totalItems: data.length,
        totalTeachingLoad: data.reduce((sum, item) => sum + (item.teaching_load || 0), 0),
        byGroup: {}
      };

      data.forEach(item => {
        const group = item.subject_group || 'Other';
        if (!stats.byGroup[group]) {
          stats.byGroup[group] = {
            count: 0,
            totalLoad: 0
          };
        }
        stats.byGroup[group].count++;
        stats.byGroup[group].totalLoad += item.teaching_load || 0;
      });

      return stats;
    } catch (error) {
      console.error('Error getting statistics:', error);
      return {
        totalItems: 0,
        totalTeachingLoad: 0,
        byGroup: {}
      };
    }
  }
}

export default new SubjectItemLoadService();
